import {
  createContext,
  useContext,
  useCallback,
  useRef,
  ReactNode,
  useMemo,
  useEffect,
  useState,
} from 'react';
import { RENDERER_VERSION } from './funnelMapSimplePreviewVersion';
import { initializePreviewStorage, setPreviewInDB } from './previewCacheStorage';
import { debugLog } from './previewDebugLogger';

/**
 * Simple hash function for creating a fingerprint from timestamps.
 * Not cryptographic, just for cache key uniqueness.
 */
const simpleHash = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  // Convert to base36 for shorter string
  return Math.abs(hash).toString(36);
};

/**
 * Extracts a fingerprint from SF funnel updatedAt timestamps.
 * Changes when any SF funnel is modified.
 */
const getFunnelsFingerprint = (funnelsData: FunnelsData | undefined): string => {
  if (!funnelsData) return '0';

  const timestamps = Object.values(funnelsData)
    .map((funnel) => funnel.updatedAt)
    .filter(Boolean)
    .sort();

  if (timestamps.length === 0) return '0';

  return simpleHash(timestamps.join('|'));
};

const getCacheKey = (funnelMap: FunnelMap): string => {
  const funnelsFingerprint = getFunnelsFingerprint(funnelMap.funnelsData);
  return `r${RENDERER_VERSION}-${funnelMap.id}-v${funnelMap.version}-f${funnelsFingerprint}`;
};

interface PreviewCacheContextValue {
  getPreviewUrl: (funnelMap: FunnelMap) => string | null;
  setPreview: (funnelMap: FunnelMap, blob: Blob) => void;
  hasPreview: (funnelMap: FunnelMap) => boolean;
  subscribe: (callback: () => void) => () => void;
  isHydrated: boolean;
}

const PreviewCacheContext = createContext<PreviewCacheContextValue | null>(null);

interface PreviewCacheProviderProps {
  children: ReactNode;
}

export const PreviewCacheProvider = ({ children }: PreviewCacheProviderProps) => {
  // Store Blobs in memory
  const blobCacheRef = useRef<Map<string, Blob>>(new Map());
  // Store object URLs for display (created from Blobs)
  const urlCacheRef = useRef<Map<string, string>>(new Map());
  const subscribersRef = useRef<Set<() => void>>(new Set());
  const [isHydrated, setIsHydrated] = useState(false);

  const notifySubscribers = useCallback(() => {
    subscribersRef.current.forEach((callback) => callback());
  }, []);

  // Hydrate blob cache from IndexedDB on mount
  useEffect(() => {
    const urlCache = urlCacheRef.current;

    const hydrate = async () => {
      debugLog('[PreviewCache]', 'Hydrating cache from IndexedDB...');
      const cachedPreviews = await initializePreviewStorage();
      blobCacheRef.current = cachedPreviews;

      // Create object URLs for all cached blobs
      cachedPreviews.forEach((blob, key) => {
        const url = URL.createObjectURL(blob);
        urlCache.set(key, url);
      });

      debugLog(
        '[PreviewCache]',
        `Hydration complete. Loaded ${cachedPreviews.size} previews from IndexedDB`,
      );
      setIsHydrated(true);
      notifySubscribers();
    };
    hydrate();

    // Cleanup: revoke all object URLs on unmount
    return () => {
      urlCache.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      urlCache.clear();
    };
  }, [notifySubscribers]);

  const getPreviewUrl = useCallback((funnelMap: FunnelMap): string | null => {
    const cacheKey = getCacheKey(funnelMap);
    const url = urlCacheRef.current.get(cacheKey) ?? null;
    debugLog(
      '[PreviewCache]',
      `getPreviewUrl: ${funnelMap.name} | key=${cacheKey} | ${url ? 'HIT' : 'MISS'}`,
    );
    return url;
  }, []);

  const setPreview = useCallback(
    (funnelMap: FunnelMap, blob: Blob): void => {
      const cacheKey = getCacheKey(funnelMap);
      debugLog(
        '[PreviewCache]',
        `setPreview: ${funnelMap.name} | key=${cacheKey} | size=${(blob.size / 1024).toFixed(1)}KB`,
      );

      // Revoke old URL if exists
      const oldUrl = urlCacheRef.current.get(cacheKey);
      if (oldUrl) {
        URL.revokeObjectURL(oldUrl);
      }

      // Store blob and create new object URL
      blobCacheRef.current.set(cacheKey, blob);
      const newUrl = URL.createObjectURL(blob);
      urlCacheRef.current.set(cacheKey, newUrl);

      notifySubscribers();

      // Write to IndexedDB (async, fire-and-forget)
      setPreviewInDB(cacheKey, blob);
    },
    [notifySubscribers],
  );

  const hasPreview = useCallback((funnelMap: FunnelMap): boolean => {
    const cacheKey = getCacheKey(funnelMap);
    const has = blobCacheRef.current.has(cacheKey);
    debugLog(
      '[PreviewCache]',
      `hasPreview: ${funnelMap.name} | key=${cacheKey} | ${has ? 'EXISTS' : 'NOT_FOUND'}`,
    );
    return has;
  }, []);

  const subscribe = useCallback((callback: () => void): (() => void) => {
    subscribersRef.current.add(callback);
    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);

  const contextValue = useMemo<PreviewCacheContextValue>(
    () => ({
      getPreviewUrl,
      setPreview,
      hasPreview,
      subscribe,
      isHydrated,
    }),
    [getPreviewUrl, setPreview, hasPreview, subscribe, isHydrated],
  );

  return (
    <PreviewCacheContext.Provider value={contextValue}>{children}</PreviewCacheContext.Provider>
  );
};

export const usePreviewCache = (): PreviewCacheContextValue => {
  const context = useContext(PreviewCacheContext);
  if (!context) {
    throw new Error('usePreviewCache must be used within a PreviewCacheProvider');
  }
  return context;
};

export const usePreviewCacheOptional = (): PreviewCacheContextValue | null => {
  return useContext(PreviewCacheContext);
};
